import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tipoMonedaService, { TipoMoneda, TipoMonedaUpdateDto } from '../../services/tipoMonedaService';

const defaultForm: TipoMoneda = {
  codigoMoneda: 0,
  descripcion: '',
  simbolo: null,
  cbufce: null,
  aliasfce: null,
  idfce: null,
};

const MonedaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoMoneda } = useParams();
  const isEdit = !!(codigoMoneda && codigoMoneda !== 'nuevo');
  const [formData, setFormData] = useState<TipoMoneda>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadMoneda = useCallback(async () => {
    if (!isEdit || !codigoMoneda) return;
    try {
      setLoading(true);
      const moneda = await tipoMonedaService.getTipoMoneda(Number(codigoMoneda));
      setFormData(moneda);
    } catch {
      enqueueSnackbar('Error al cargar la moneda', { variant: 'error' });
      navigate('/sistema/monedas');
    } finally {
      setLoading(false);
    }
  }, [isEdit, codigoMoneda, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isEdit) loadMoneda();
  }, [isEdit, loadMoneda]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'codigoMoneda') {
      const num = parseInt(value, 10);
      setFormData((prev) => ({ ...prev, codigoMoneda: isNaN(num) ? 0 : num }));
    } else if (name === 'descripcion') {
      setFormData((prev) => ({ ...prev, descripcion: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value.trim() === '' ? null : value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const descripcion = formData.descripcion?.trim();
    if (!descripcion) {
      enqueueSnackbar('La descripción es requerida', { variant: 'warning' });
      return;
    }
    if (!isEdit && (formData.codigoMoneda < 1 || formData.codigoMoneda > 255)) {
      enqueueSnackbar('El código debe estar entre 1 y 255', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      if (isEdit && codigoMoneda) {
        const updateDto: TipoMonedaUpdateDto = {
          descripcion,
          simbolo: formData.simbolo?.trim() || null,
          cbufce: formData.cbufce?.trim() || null,
          aliasfce: formData.aliasfce?.trim() || null,
          idfce: formData.idfce?.trim() || null,
        };
        await tipoMonedaService.updateTipoMoneda(Number(codigoMoneda), updateDto);
      } else {
        await tipoMonedaService.createTipoMoneda({
          ...formData,
          descripcion,
          simbolo: formData.simbolo?.trim() || null,
          cbufce: formData.cbufce?.trim() || null,
          aliasfce: formData.aliasfce?.trim() || null,
          idfce: formData.idfce?.trim() || null,
        });
      }
      enqueueSnackbar('Moneda guardada exitosamente', { variant: 'success' });
      navigate('/sistema/monedas');
    } catch (error: unknown) {
      console.error('Error al guardar moneda:', error);
      const err = error as { response?: { data?: string | { message?: string } } };
      const msg = typeof err?.response?.data === 'string'
        ? err.response.data
        : err?.response?.data?.message || 'Error al guardar la moneda';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sistema/monedas')}
              sx={{ textTransform: 'none' }}
            >
              Monedas
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isEdit ? 'Editar Moneda' : 'Nueva Moneda'}
            </Typography>
          </Stack>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={2}>
                <TextField
                  name="codigoMoneda"
                  label="Código"
                  type="number"
                  value={formData.codigoMoneda || ''}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={isEdit}
                  inputProps={{ min: 1, max: 255 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  name="descripcion"
                  label="Descripción"
                  value={formData.descripcion}
                  onChange={handleChange}
                  fullWidth
                  required
                  inputProps={{ maxLength: 20 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  name="simbolo"
                  label="Símbolo"
                  value={formData.simbolo || ''}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ maxLength: 5 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  name="idfce"
                  label="ID FCE"
                  value={formData.idfce || ''}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ maxLength: 20 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="cbufce"
                  label="CBU FCE"
                  value={formData.cbufce || ''}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ maxLength: 22 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="aliasfce"
                  label="Alias FCE"
                  value={formData.aliasfce || ''}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ maxLength: 150 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/sistema/monedas')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MonedaForm;
